import api from './api';

// Admin-specific types matching backend models
export interface AdminMember {
  id: number;
  firstName: string;
  lastName: string;
  color: string;
  shirtNumber: number;
  photoVersion?: number | null;
}

export interface AdminPunishment {
  id: number;
  type: string;
  value: number;
}

export interface AdminMemberPunishment {
  id: number;
  memberId: number;
  punishmentId: number;
  value: number;
  date: string;
  member?: AdminMember;
  punishment?: AdminPunishment;
}

export interface AdminPresence {
  id: number;
  memberId: number;
  date: string;
  member?: AdminMember;
}

export interface AdminSubsidy {
  id: number;
  value: number;
  date: string;
}

export interface AdminRule {
  id: number;
  type: string;
  ruleDescription: string;
  order: number;
}

class AdminService {
  // Members
  async getMembers(): Promise<AdminMember[]> {
    return api.get<AdminMember[]>('/admin/members');
  }

  async getMember(id: number): Promise<AdminMember> {
    return api.get<AdminMember>(`/admin/members/${id}`);
  }

  async createMember(member: Omit<AdminMember, 'id'>): Promise<AdminMember> {
    return api.post<AdminMember>('/admin/members', member);
  }

  async updateMember(id: number, member: Partial<AdminMember>): Promise<AdminMember> {
    return api.put<AdminMember>(`/admin/members/${id}`, member);
  }

  async deleteMember(id: number): Promise<void> {
    return api.delete<void>(`/admin/members/${id}`);
  }

  async uploadMemberPhoto(id: number, photo: Blob): Promise<{ photoVersion: number }> {
    const form = new FormData();
    form.append('file', photo, 'photo.jpg');
    return api.putForm<{ photoVersion: number }>(`/admin/members/${id}/photo`, form);
  }

  async deleteMemberPhoto(id: number): Promise<void> {
    return api.delete<void>(`/admin/members/${id}/photo`);
  }

  // Punishments
  async getPunishments(): Promise<AdminPunishment[]> {
    return api.get<AdminPunishment[]>('/admin/punishments');
  }

  async getPunishment(id: number): Promise<AdminPunishment> {
    return api.get<AdminPunishment>(`/admin/punishments/${id}`);
  }

  async createPunishment(punishment: Omit<AdminPunishment, 'id'>): Promise<AdminPunishment> {
    return api.post<AdminPunishment>('/admin/punishments', punishment);
  }

  async updatePunishment(id: number, punishment: Partial<AdminPunishment>): Promise<AdminPunishment> {
    return api.put<AdminPunishment>(`/admin/punishments/${id}`, punishment);
  }

  async deletePunishment(id: number): Promise<void> {
    return api.delete<void>(`/admin/punishments/${id}`);
  }

  // Member Punishments
  async getMemberPunishments(year?: string): Promise<AdminMemberPunishment[]> {
    const url = year ? `/admin/member-punishments?year=${year}` : '/admin/member-punishments';
    return api.get<AdminMemberPunishment[]>(url);
  }

  async getMemberPunishment(id: number): Promise<AdminMemberPunishment> {
    return api.get<AdminMemberPunishment>(`/admin/member-punishments/${id}`);
  }

  async createMemberPunishment(memberPunishment: Omit<AdminMemberPunishment, 'id' | 'member' | 'punishment'>): Promise<AdminMemberPunishment> {
    return api.post<AdminMemberPunishment>('/admin/member-punishments', memberPunishment);
  }

  async updateMemberPunishment(id: number, memberPunishment: Partial<AdminMemberPunishment>): Promise<AdminMemberPunishment> {
    return api.put<AdminMemberPunishment>(`/admin/member-punishments/${id}`, memberPunishment);
  }

  async deleteMemberPunishment(id: number): Promise<void> {
    return api.delete<void>(`/admin/member-punishments/${id}`);
  }

  // Presences
  async getPresences(year?: string): Promise<AdminPresence[]> {
    const url = year ? `/admin/presences?year=${year}` : '/admin/presences';
    return api.get<AdminPresence[]>(url);
  }

  async getPresence(id: number): Promise<AdminPresence> {
    return api.get<AdminPresence>(`/admin/presences/${id}`);
  }

  async createPresence(presence: Omit<AdminPresence, 'id' | 'member'>): Promise<AdminPresence> {
    return api.post<AdminPresence>('/admin/presences', presence);
  }

  async updatePresence(id: number, presence: Partial<AdminPresence>): Promise<AdminPresence> {
    return api.put<AdminPresence>(`/admin/presences/${id}`, presence);
  }

  async deletePresence(id: number): Promise<void> {
    return api.delete<void>(`/admin/presences/${id}`);
  }

  // Subsidies
  async getSubsidies(year?: string): Promise<AdminSubsidy[]> {
    const url = year ? `/admin/subsidies?year=${year}` : '/admin/subsidies';
    return api.get<AdminSubsidy[]>(url);
  }

  async getSubsidy(id: number): Promise<AdminSubsidy> {
    return api.get<AdminSubsidy>(`/admin/subsidies/${id}`);
  }

  async createSubsidy(subsidy: Omit<AdminSubsidy, 'id'>): Promise<AdminSubsidy> {
    return api.post<AdminSubsidy>('/admin/subsidies', subsidy);
  }

  async updateSubsidy(id: number, subsidy: Partial<AdminSubsidy>): Promise<AdminSubsidy> {
    return api.put<AdminSubsidy>(`/admin/subsidies/${id}`, subsidy);
  }

  async deleteSubsidy(id: number): Promise<void> {
    return api.delete<void>(`/admin/subsidies/${id}`);
  }

  // Rules
  async getRules(): Promise<AdminRule[]> {
    return api.get<AdminRule[]>('/admin/rules');
  }

  async getRule(id: number): Promise<AdminRule> {
    return api.get<AdminRule>(`/admin/rules/${id}`);
  }

  async createRule(rule: Omit<AdminRule, 'id'>): Promise<AdminRule> {
    return api.post<AdminRule>('/admin/rules', rule);
  }

  async updateRule(id: number, rule: Partial<AdminRule>): Promise<AdminRule> {
    return api.put<AdminRule>(`/admin/rules/${id}`, rule);
  }

  async deleteRule(id: number): Promise<void> {
    return api.delete<void>(`/admin/rules/${id}`);
  }
}

export default new AdminService();

